/**
 * KAN-870: проверка новой версии приложения после выкатки.
 * Сверяет APP_VERSION с /version.json при монтировании, при возврате вкладки
 * и раз в intervalMs, пока вкладка видима. Ошибки молча игнорируются,
 * найденное обновление не сбрасывается, после него опрос останавливается.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { APP_VERSION, isNewVersion } from '@/lib/appVersion';

const DEFAULT_INTERVAL_MS = 5 * 60 * 1000;
/** Сколько ждать registration.update() перед перезагрузкой. */
export const SW_UPDATE_TIMEOUT_MS = 3000;

export type FetchVersion = () => Promise<string | null>;

export interface UseAppUpdateOptions {
  intervalMs?: number;
  fetchVersion?: FetchVersion;
}

export interface UseAppUpdateResult {
  updateAvailable: boolean;
  reload: () => void;
}

/** Версия с сервера из /version.json, мимо всех кэшей. */
export async function fetchServerVersion(): Promise<string | null> {
  const response = await fetch(`/version.json?ts=${Date.now()}`, { cache: 'no-store' });
  if (!response.ok) return null;
  const data: unknown = await response.json();
  if (data && typeof data === 'object' && 'version' in data) {
    const { version } = data as { version: unknown };
    return typeof version === 'string' ? version : null;
  }
  return null;
}

/** Обновляет service worker (если есть) и перезагружает страницу. */
export async function reloadApp(): Promise<void> {
  try {
    const sw = (navigator as Navigator & { serviceWorker?: ServiceWorkerContainer }).serviceWorker;
    const registration = sw ? await sw.getRegistration() : undefined;
    if (registration) {
      // зависший update() не должен блокировать кнопку
      await Promise.race([
        registration.update(),
        new Promise<void>((resolve) => setTimeout(resolve, SW_UPDATE_TIMEOUT_MS)),
      ]);
    }
  } catch {
    // обновление service worker не обязательно для перезагрузки
  }
  window.location.reload();
}

export function useAppUpdate({
  intervalMs = DEFAULT_INTERVAL_MS,
  fetchVersion = fetchServerVersion,
}: UseAppUpdateOptions = {}): UseAppUpdateResult {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const fetchRef = useRef(fetchVersion);
  fetchRef.current = fetchVersion;

  useEffect(() => {
    // обновление уже найдено: дальше сервер не опрашиваем
    if (updateAvailable) return undefined;
    let active = true;
    const check = async () => {
      try {
        const remote = await fetchRef.current();
        if (active && isNewVersion(APP_VERSION, remote)) setUpdateAvailable(true);
      } catch {
        // сеть или битый ответ: попробуем в следующий раз
      }
    };
    const isVisible = () => document.visibilityState !== 'hidden';
    const onVisibility = () => {
      if (isVisible()) void check();
    };

    void check();
    document.addEventListener('visibilitychange', onVisibility);
    const timer = window.setInterval(() => {
      if (isVisible()) void check();
    }, intervalMs);
    return () => {
      active = false;
      document.removeEventListener('visibilitychange', onVisibility);
      window.clearInterval(timer);
    };
  }, [intervalMs, updateAvailable]);

  const reload = useCallback(() => {
    void reloadApp();
  }, []);

  return { updateAvailable, reload };
}
